import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Space, Tooltip, Button } from 'antd';
import moment from 'moment';

import ModalCreate from './Create';
import ModalUpdate from './Update';

import { handleTaskCounter } from '~/pages/Task/Card/functions';

import { sprintReadRequest } from '~/store/modules/sprint/actions';
import { taskHourCreateRequest } from '~/store/modules/taskHour/actions';
import { taskPlanningIndexRequest, taskPlanningReadRequest, taskPlanningDeleteRequest } from '~/store/modules/taskPlanning/actions';

import { PlusCircleOutlined, DeleteFilled, CheckOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Container, DataList } from './styles';

export default function TaskWork({ projectId, taskId, sprintId, users, playing }) {
  const dispatch = useDispatch();

  const loading = useSelector(store => store.taskPlanning.loading);
  const dataSource = useSelector(store => store.taskPlanning.list);
  const taskHour = useSelector(store => store.taskHour.playing);

  const [id, setId] = useState();
  const [isVisibleCreate, setIsVisibleCreate] = useState(false);
  const [isVisibleUpdate, setIsVisibleUpdate] = useState(false);
  const [start, setStart] = useState(true);

  function onTimeUpdate(id, start = true) {
    setStart(start);
    setId(id);
    dispatch(taskPlanningReadRequest(id, taskId, projectId));

    setIsVisibleUpdate(true);
  }

  function onTimeDelete(id) {
    if (window.confirm('Deseja realmente excluir este registro de tempo?')) {
      dispatch(taskPlanningDeleteRequest(id, taskId, projectId));
    }
  }

  function onPlay(projectId, taskId, planningId) {
    dispatch(taskHourCreateRequest({ projectId, taskId, planningId }));
    dispatch(sprintReadRequest(projectId, sprintId));
  }


  // PRIMEIRO: Crie funções auxiliares no topo do componente (ou em um arquivo separado)
  const canEditTime = (record) => {
    const isAllowed = record.allowed === true || Number(record.allowed) === 1;
    const isNotFinished = !(record.finished === true || Number(record.finished) === 1);
    return isAllowed && isNotFinished;
  };

  const isFinished = (record) => {
    return record.finished === true || Number(record.finished) === 1;
  };

  const isAllowed = (record) => {
    return record.allowed === true || Number(record.allowed) === 1;
  };

  // Função para calcular o esforço total (se usado em outros lugares)
  const calculateTotalEffort = (data) => {
    return data?.reduce((acc, item) => acc + parseInt(item.effort || 0), 0) || 0;
  };

  // Componente UserAvatar separado
  const UserAvatar = ({ userId, users }) => {
    const user = users?.find(u => u.id === userId);
    if (!user) return null;

    return (
      <Tooltip title={user.name}>
        <Avatar size="18" style={{ backgroundColor: user.color, verticalAlign: 'middle' }}>
          {user.short}
        </Avatar>
      </Tooltip>
    );
  };

  // Componente TimeCell separado
  const TimeCell = ({ record, field, onUpdate }) => {
    const time = record[field];
    const editable = canEditTime(record);

    const handleClick = () => {
      if (editable && time) {
        onUpdate(record.id, field === 'startAt');
      }
    };

    return (
      <Space
        className={editable ? 'editable' : ''}
        onClick={handleClick}
      >
        {moment(time).format('DD/MM/YYYY HH:mm')}
      </Space>
    );
  };

  // Componente ActionsCell separado
  const ActionsCell = ({ record, onDelete, onCreate }) => {
    const finished = isFinished(record);
    const allowed = isAllowed(record);

    return (
      <Space align='right'>
        {finished && (
          <Tooltip title="Planejamento contabilizado">
            <CheckOutlined className='success' />
          </Tooltip>
        )}

        {allowed && !finished && (
          <>
            {!playing && (
              <Tooltip title="Iniciar contagem de tempo">
                <PlayCircleOutlined onClick={() => onPlay(projectId, taskId, record.id)} />
              </Tooltip>
            )}

            { }
            <Tooltip title="Excluir item do planejamento">
              <DeleteFilled
                className='danger'
                onClick={() => onDelete(record.id)}
              />
            </Tooltip>
          </>
        )}
      </Space>
    );
  };

  useMemo(() => {
    dispatch(taskPlanningIndexRequest(taskId, projectId));
  }, [taskId, taskHour]);

  return (
    <Container>
      <div className='w-100 text-center'>
        <DataList
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          footer={() => (
            <Space className='data-list-footer'>
              {handleTaskCounter(calculateTotalEffort(dataSource))}
            </Space>
          )}
        >
          <DataList.Column
            dataIndex="id"
            key="id"
            render={(_, record) => (
              <UserAvatar userId={record.userId} users={users} />
            )}
          />

          <DataList.Column
            title="Início"
            dataIndex="startAt"
            key="startAt"
            render={(_, record) => (
              <TimeCell
                record={record}
                field="startAt"
                onUpdate={onTimeUpdate}
              />
            )}
          />

          <DataList.Column
            title="Término"
            dataIndex="endAt"
            key="endAt"
            render={(_, record) => (
              <TimeCell
                record={record}
                field="endAt"
                onUpdate={onTimeUpdate}
              />
            )}
          />

          <DataList.Column
            title={
              <Tooltip title="Adicionar intervalo">
                <Button
                  htmlType='button'
                  type='ghost'
                  icon={<PlusCircleOutlined />}
                  onClick={() => setIsVisibleCreate(true)}
                />
              </Tooltip>
            }
            dataIndex="allowed"
            key="allowed"
            render={(_, record) => (
              <ActionsCell
                record={record}
                onDelete={onTimeDelete}
                onCreate={() => setIsVisibleCreate(true)}
              />
            )}
          />
        </DataList>
      </div>

      <ModalCreate isVisible={isVisibleCreate} setIsVisible={setIsVisibleCreate} projectId={projectId} taskId={taskId} />
      <ModalUpdate isVisible={isVisibleUpdate} setIsVisible={setIsVisibleUpdate} projectId={projectId} taskId={taskId} id={id} start={start} />

    </Container>
  );
}
